import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { baseClient } from '../api/baseClient';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('🔐 AuthProvider component rendering...');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  console.log('🔐 AuthProvider initial state - loading:', loading, 'user:', user);

  useEffect(() => {
    console.log('🔍 Setting up auth state listener...');
    let authResolved = false;
    
    // CHECK FOR TOKEN FROM LANDING PAGE (SSO)
    const checkIncomingToken = async () => {
      // Check URL params for token
      const urlParams = new URLSearchParams(window.location.search);
      const incomingToken = urlParams.get('token');
      
      if (incomingToken) {
        console.log('🎫 Received token from landing page, validating...');
        try {
          // Store token
          localStorage.setItem('firebaseToken', incomingToken);
          
          // Clean URL (remove token from address bar for security)
          window.history.replaceState({}, document.title, window.location.pathname);
          
          console.log('✅ Token stored, Firebase will auto-authenticate');
        } catch (error) {
          console.error('❌ Error handling incoming token:', error);
        }
      }
    };
    
    checkIncomingToken();
    
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(
      auth, 
      (user) => {
        console.log('🔄 Auth state changed:', user ? `User: ${user.email}` : 'No user');
        authResolved = true;
        setUser(user);
        setLoading(false);
        
        // Store token in localStorage for API calls
        if (user) {
          user.getIdToken().then(async (token) => {
            localStorage.setItem('firebaseToken', token);
            console.log('✅ Token stored for user:', user.email);

            // Post-login referral tracking: if a ref code was captured from the
            // landing URL, record the signup against that affiliate then clear it.
            const pendingRef = localStorage.getItem('crm_ref');
            if (pendingRef) {
              try {
                await baseClient.post('/referrals/track', {
                  referral_code: pendingRef,
                  referred_user_id: user.uid,
                  referred_email: user.email ?? undefined,
                });
                console.log('✅ Referral tracked for code:', pendingRef);
              } catch (err) {
                console.warn('⚠️ Referral tracking failed (non-blocking):', err);
              } finally {
                localStorage.removeItem('crm_ref');
              }
            }
          });
        } else {
          // Don't clear SSO token if it was just set from landing page
          const ssoToken = localStorage.getItem('firebaseToken');
          if (!ssoToken) {
            localStorage.removeItem('firebaseToken');
          } else {
            console.log('🎫 Keeping SSO token from landing page (no Firebase session on this domain)');
          }
        }
      }, 
      (error) => {
        // Handle Firebase initialization errors gracefully
        console.warn('⚠️ Firebase auth error (continuing anyway):', error);
        authResolved = true;
        setUser(null);
        setLoading(false);
      }
    );

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (!authResolved) {
        console.warn('⚠️ Firebase auth timeout after 3 seconds - forcing loading to false');
        setLoading(false);
      }
    }, 3000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('🔵 Starting Google sign-in popup...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Sign-in popup completed');
      
      const token = await getIdToken(result.user);
      localStorage.setItem('firebaseToken', token);
      console.log('✅ Firebase sign-in successful, user:', result.user.email);
      console.log('✅ Token stored, auth state will update automatically');
    } catch (error: any) {
      console.error('❌ Error signing in with Google:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('firebaseToken');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const getToken = async (): Promise<string | null> => {
    if (!user) return null;
    try {
      const token = await getIdToken(user, true); // Force refresh
      localStorage.setItem('firebaseToken', token);
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
